// Runtime configuration, resolved once at module load.
//
// window.__ENV__ is written by docker-entrypoint.sh at container start from the
// k8s deployment env, so the same image serves every environment. process.env is
// the Parcel build-time fallback (baked in via the Dockerfile ARG) for local dev
// and any image built before the runtime mechanism existed.

const runtime = (typeof window !== 'undefined' && window.__ENV__) || {};

// Full GraphQL endpoint including /graphql: callers POST to it directly, and the
// WebSocket base is derived by stripping that suffix.
export const BFF_URL =
  runtime.BFF_URL || process.env.BFF_URL || 'https://api.parsinabot.ir/graphql';

export const BFF_WS_BASE =
  process.env.BFF_WS_URL ||
  BFF_URL.replace(/^https/, 'wss').replace(/^http/, 'ws').replace('/graphql', '');

// Keycloak origin, no realm path — auth.js appends /realms/<realm>. This used to
// be a bare constant in auth.js, which meant moving SSO host required editing and
// rebuilding the client; routing it through here lets the deployment decide.
// KEYCLOAK_URL is the brand-neutral name and wins; PARSINA_KC_BASE_URL is the one
// every existing deployment sets, and it keeps working.
export const KC_BASE_URL =
  runtime.KEYCLOAK_URL ||
  runtime.PARSINA_KC_BASE_URL ||
  process.env.PARSINA_KC_BASE_URL ||
  'https://sso.itechtrade.ir';

// ── Keycloak identity: realm, client, roles client ──────────────────────────
// Which realm this panel signs in to, which client it signs in as, and which
// client's roles the BFFs read off the token. Parsina's own values are the
// fallbacks, so an image run with none of these set signs in exactly as before;
// a deployment on another realm sets them at container start, same image.
//
// Each lookup takes the first value that is set: the brand-neutral name in
// env.js, the legacy PARSINA_* name in env.js, what the image was built with,
// today's literal. docker-entrypoint.sh writes these keys only when they are
// set, so "nothing set" really does reach the build-time value.
//
// Read on every call, not once at load like the values above: what decides is
// the env.js of the page that is running, and a test can set its own.
const runtimeNow = () => (typeof window !== 'undefined' && window.__ENV__) || {};

/**
 * Whether the gift-code card is offered on the Credits screen. Gift codes are the
 * marketing service's (the doctor BFF redeems them there), and a deployment
 * without that service sets GIFT_CODES=off: the card would only answer «not
 * valid» to every code. Anything else, or nothing — every Parsina deployment —
 * keeps it. The BFF does not say whether it can reach the marketing service, so
 * the deployment that leaves the service out says so here (PM-0211).
 */
export function giftCodesEnabled() {
  const value = String(runtimeNow().GIFT_CODES || '').trim().toLowerCase();
  return value !== 'off' && value !== 'false' && value !== '0';
}

/** The realm: the path segment after /realms/. */
export function kcRealm() {
  const env = runtimeNow();
  return env.KEYCLOAK_REALM || env.PARSINA_KC_REALM
    || process.env.KEYCLOAK_REALM || process.env.PARSINA_KC_REALM
    || 'parsina';
}

/**
 * The OIDC client this panel signs in as.
 *
 * The image bakes one in at build time (Dockerfile ARG PARSINA_KC_CLIENT_ID,
 * default `parsina-doctor`), and that baked value is what production signs in
 * with; `parsina-web` is only what a build without it gets (`npm run dev`).
 * There is deliberately no build-time KEYCLOAK_CLIENT_ID: Parcel inlines the
 * whole build environment, and in a BFF's environment that name means the roles
 * client, so a local build from such a shell would sign in as `parsina-bff`.
 */
export function kcClientId() {
  const env = runtimeNow();
  return env.KEYCLOAK_CLIENT_ID || env.PARSINA_KC_CLIENT_ID
    || process.env.PARSINA_KC_CLIENT_ID
    || 'parsina-web';
}

/**
 * The client whose roles the BFFs grant and read (`resource_access[<it>].roles`).
 * It has to name the same client as the BFFs' own setting, or the panel waits
 * for a role that was granted under another name.
 */
export function kcBffClientId() {
  return runtimeNow().KEYCLOAK_BFF_CLIENT_ID || 'parsina-bff';
}

// Origin of the CLINIC panel (clinic-app/parsina.client.clinic), for the
// «رفتن به پنل کلینیک» switch in the sidebar. A separate origin per environment
// — clinic.parsinabot.ir / beta1.clinic… / beta.clinic… — so it must come from
// the deployment, exactly like BFF_URL, rather than being baked into the image.
//
// The switch is a plain navigation, not a token hand-off: both panels sign in to
// the same realm (each as its own client, see kcClientId), so the clinic app runs
// its own PKCE flow and Keycloak's SSO session returns a token without a second
// sign-in. No trailing slash — callers append the path.
export const CLINIC_APP_URL = (
  runtime.CLINIC_APP_URL ||
  process.env.CLINIC_APP_URL ||
  'https://clinic.parsinabot.ir'
).replace(/\/+$/, '');

// ── The map in «صفحهٔ من» › اطلاعات مطب ─────────────────────────────────────
// The same two settings, defaults and validation as the clinic panel's
// LocationPicker (clinic-app src/lib/runtimeEnv.js), so one mirror serves both.
//
// MAP_TILE_URL  the Leaflet tile template. A value without {z}/{x}/{y} is not a
//               tile template and falls back to the default instead of drawing
//               grey squares (an entrypoint once truncated it to "https://{s").
// MAP_LIB_BASE  where leaflet.js/.css are loaded from, at runtime: the map is
//               optional, and a library most sessions never open should not
//               sit in the entry bundle. Overridable for a network that cannot
//               reach the CDN.
const isTileTemplate = (v) => typeof v === 'string' && /\{z\}/.test(v) && /\{x\}/.test(v) && /\{y\}/.test(v);

export const MAP_TILE_URL =
  [runtime.MAP_TILE_URL, process.env.MAP_TILE_URL].find(isTileTemplate) ||
  'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

export const MAP_LIB_BASE = (
  runtime.MAP_LIB_BASE || process.env.MAP_LIB_BASE || 'https://unpkg.com/leaflet@1.9.4/dist'
).replace(/\/+$/, '');

// Origin of حساب پارسینا — where a person edits their own identity, password,
// language and two-factor, for every panel at once. Supplied per environment by
// the deployment, exactly like BFF_URL, rather than baked into the image: a
// beta1 panel linking to the staging account app would hand someone a session
// against another environment's data.
//
// Like the clinic switch, this is a plain navigation and not a token hand-off —
// same realm, so the account app runs its own PKCE flow and Keycloak's SSO
// session returns a token without a second sign-in.
//
// No trailing slash — callers append the path.
export const ACCOUNT_APP_URL = (
  runtime.ACCOUNT_APP_URL ||
  process.env.ACCOUNT_APP_URL ||
  'https://account.parsinabot.ir'
).replace(/\/+$/, '');

/**
 * A link into the account app that can find its way back here.
 *
 * `return_url` is checked SERVER-SIDE by the account app against its own
 * allowlist; this origin is already on it for both environments. An unlisted
 * host does not error — the «بازگشت» chip simply does not render — so a new
 * panel hostname needs adding there before its link is useful.
 */
export function accountUrl(section = '/') {
  const back = typeof window !== 'undefined' ? window.location.origin : '';
  const path = section.startsWith('/') ? section : `/${section}`;
  return `${ACCOUNT_APP_URL}${path}${back ? `?return_url=${encodeURIComponent(back)}` : ''}`;
}
